import { action, observable } from 'mobx'
import { observer } from 'mobx-react'
import React, { Component, Fragment } from 'react'
import styled from 'styled-components'
import Button from '../components/Button'
import Card from '../components/Card'
import Paragraph from '../components/Paragraph'
import Text from '../components/Text'
import patient from '../stores/patient'
import lang from '../stores/lang'

const VideoFrame = styled.iframe`
  width: 100%;
  height: 22.5rem;
  border: 0;
`

function getVideoSrc(locale) {
  if (locale === 'es_US') {
    switch (patient.fields.diagnosis) {
      case 'ANGLE_CLOSURE':
        return 'https://www.youtube-nocookie.com/embed/E9eirC6IGzI?rel=0&showinfo=0&modestbranding=1'
      case 'NEOVASCULAR':
        return 'https://www.youtube-nocookie.com/embed/mauG__j5yEo?rel=0&showinfo=0&modestbranding=1'
      case 'NORMAL_TENSION':
        return 'https://www.youtube-nocookie.com/embed/8X3KjSWlDlU?rel=0&showinfo=0&modestbranding=1'
      case 'OPEN_ANGLE':
        return 'https://www.youtube-nocookie.com/embed/6VaPnMg4SAg?rel=0&showinfo=0&modestbranding=1'
      case 'OCULAR_HYPERTENSION':
        return 'https://www.youtube-nocookie.com/embed/TFqZ8Sj2adI?rel=0&showinfo=0&modestbranding=1'
      case 'PIGMENTARY':
        return 'https://www.youtube-nocookie.com/embed/Q08QsfORGAo?rel=0&showinfo=0&modestbranding=1'
      case 'PSEUDOEXFOLIATION':
        return 'https://www.youtube-nocookie.com/embed/vTvv7eK2kqQ?rel=0&showinfo=0&modestbranding=1'
      case 'STEROID_RESPONSE':
        return 'https://www.youtube-nocookie.com/embed/FnoWjoMonSY?rel=0&showinfo=0&modestbranding=1'
      case 'SUSPECT':
        return 'https://www.youtube-nocookie.com/embed/NTWWzJfSqos?rel=0&showinfo=0&modestbranding=1'
      case 'TRAUMATIC':
        return 'https://www.youtube-nocookie.com/embed/6RkcjunzfEE?rel=0&showinfo=0&modestbranding=1'
      case 'UVEITIC':
        return 'https://www.youtube-nocookie.com/embed/USNFsvueIFA?rel=0&showinfo=0&modestbranding=1'
      default:
        throw new Error("couldn't get video source, patient.fields.diagnosis was invalid")
    }
  }
  switch (patient.fields.diagnosis) {
    case 'ANGLE_CLOSURE':
      return 'https://www.youtube-nocookie.com/embed/O8YwnH82Wec?rel=0&showinfo=0&modestbranding=1'
    case 'NEOVASCULAR':
      return 'https://www.youtube-nocookie.com/embed/c9dwHg6B5jQ?rel=0&showinfo=0&modestbranding=1'
    case 'NORMAL_TENSION':
      return 'https://www.youtube-nocookie.com/embed/1e3nSx1_krY?rel=0&showinfo=0&modestbranding=1'
    case 'OCULAR_HYPERTENSION':
      return 'https://www.youtube-nocookie.com/embed/FrWdqQ4dVBE?rel=0&showinfo=0&modestbranding=1'
    case 'OPEN_ANGLE':
      return 'https://www.youtube-nocookie.com/embed/Pj8_vrTAcLo?rel=0&showinfo=0&modestbranding=1'
    case 'PIGMENTARY':
      return 'https://www.youtube-nocookie.com/embed/tGAYNHB0YgQ?rel=0&showinfo=0&modestbranding=1'
    case 'PSEUDOEXFOLIATION':
      return 'https://www.youtube-nocookie.com/embed/HztxaQZg9wg?rel=0&showinfo=0&modestbranding=1'
    case 'STEROID_RESPONSE':
      return 'https://www.youtube-nocookie.com/embed/AEj7z2sK-dA?rel=0&showinfo=0&modestbranding=1'
    case 'SUSPECT':
      return 'https://www.youtube-nocookie.com/embed/PqveqasqRR8?rel=0&showinfo=0&modestbranding=1'
    case 'TRAUMATIC':
      return 'https://www.youtube-nocookie.com/embed/BpxGNqfsSn4?rel=0&showinfo=0&modestbranding=1'
    case 'UVEITIC':
      return 'https://www.youtube-nocookie.com/embed/cwRGmHOLTCo?rel=0&showinfo=0&modestbranding=1'
    default:
      throw new Error("couldn't get video source, patient.fields.diagnosis was invalid")
  }
}

function renderScript() {
  if (patient.isCombat) {
    return (
      <Fragment>
        <Paragraph str="prose.combatVideoScript1" />
        <Paragraph str="prose.combatVideoScript2" />
        <Paragraph str="prose.combatVideoScript3" />
      </Fragment>
    );
  }
  switch (patient.fields.diagnosis) {
    case 'ANGLE_CLOSURE':
      return (
        <Fragment>
          <Paragraph str="prose.videoScriptAngleClosure" />
          <Paragraph str="prose.videoScriptOpticNerve" />
          <Paragraph str="prose.videoScriptDoctorRecommend" />
        </Fragment>
      );
    case 'NEOVASCULAR':
      return (
        <Fragment>
          <Paragraph str="prose.videoScriptNeovascular1" />
          <Paragraph str="prose.videoScriptNeovascular2" />
          <Paragraph str="prose.videoScriptOpticNerve" />
          <Paragraph str="prose.videoScriptDoctorRecommend" />
        </Fragment>
      );
    case 'NORMAL_TENSION':
      return (
        <Fragment>
          <Paragraph str="prose.videoScriptNormalTension" />
          <Paragraph str="prose.videoScriptOpticNerve" />
          <Paragraph str="prose.videoScriptDoctorRecommend" />
        </Fragment>
      );
    case 'OCULAR_HYPERTENSION':
      return (
        <Fragment>
          <Paragraph str="prose.videoScriptOcularHypertension1" />
          <Paragraph str="prose.videoScriptOpticNerve" />
          <Paragraph str="prose.videoScriptOcularHypertension2" />
        </Fragment>
      );
    case 'OPEN_ANGLE':
      return (
        <Fragment>
          <Paragraph str="prose.videoScriptOpenAngle" />
          <Paragraph str="prose.videoScriptOpticNerve" />
          <Paragraph str="prose.videoScriptDoctorRecommend" />
        </Fragment>
      );
    case 'PIGMENTARY':
      return (
        <Fragment>
          <Paragraph str="prose.videoScriptPigmentary" />
          <Paragraph str="prose.videoScriptOpticNerve" />
          <Paragraph str="prose.videoScriptDoctorRecommend" />
        </Fragment>
      );
    case 'PSEUDOEXFOLIATION':
      return (
        <Fragment>
          <Paragraph str="prose.videoScriptPseudo" />
          <Paragraph str="prose.videoScriptOpticNerve" />
          <Paragraph str="prose.videoScriptDoctorRecommend" />
        </Fragment>
      );
    case 'STEROID_RESPONSE':
      return (
        <Fragment>
          <Paragraph str="prose.videoScriptSteroidResponse1" />
          <Paragraph str="prose.videoScriptSteroidResponse2" />
          <Paragraph str="prose.videoScriptOpticNerve" />
          <Paragraph str="prose.videoScriptDoctorRecommend" />
        </Fragment>
      );
    case 'SUSPECT':
      return (
        <Fragment>
          <Paragraph str="prose.videoScriptSuspect1" />
          <Paragraph str="prose.videoScriptOpticNerve" />
          <Paragraph str="prose.videoScriptSuspect2" />
        </Fragment>
      );
    case 'TRAUMATIC':
      return (
        <Fragment>
          <Paragraph str="prose.videoScriptTraumatic" />
          <Paragraph str="prose.videoScriptOpticNerve" />
          <Paragraph str="prose.videoScriptDoctorRecommend" />
        </Fragment>
      );
    case 'UVEITIC':
      return (
        <Fragment>
          <Paragraph str="prose.videoScriptUveitic" />
          <Paragraph str="prose.videoScriptOpticNerve" />
          <Paragraph str="prose.videoScriptDoctorRecommend" />
        </Fragment>
      );
    default:
      throw new Error("couldn't render video script, patient.fields.diagnosis was invalid");
  }
}

@observer
export default class Video extends Component {
  @observable scriptShown = false

  @action
  toggleScript = () => {
    this.scriptShown = !this.scriptShown
  }

  render() {
    const { screeningSession } = this.props

    const videoSrc = () => {
      if(patient.isCombat) {
        return 'https://www.youtube-nocookie.com/embed/5A-SwrgfIVI?si=qBmOMs8f8agBwp8E'
      } else {
        return screeningSession && patient.fields.lowVision === "YES"
          ? 'https://www.youtube-nocookie.com/embed/Ifa9RyHsY6s?rel=0&showinfo=0&modestbranding=1'
          : getVideoSrc(lang.locale)
      }
    }
    return (
      <Card>
        <Card.Title>
          {!screeningSession && <Text str="prose.youHaveBeenDiagnosedWith" />}
          <b style={{ marginLeft: '0.5rem' }}><Text str={`choices.diagnosis.${patient.fields.diagnosis}`} /></b>
        </Card.Title>
        <Card.Pad>
          <VideoFrame
            title="learn-video"
            src={videoSrc()}
          />
        </Card.Pad>
        <Card.Divider />
        <Card.Pad>
          <Button color="blue" onClick={this.toggleScript}>
            {!this.scriptShown &&
              <Fragment>
                <i className="fas fa-file-alt" style={{ marginRight: '0.5rem' }} />
                <Text str="titles.showScript" />
              </Fragment>
            }
            {this.scriptShown &&
              <Fragment>
                <i className="fas fa-times" style={{ marginRight: '0.5rem' }} />
                <Text str="titles.hideScript" />
              </Fragment>
            }
          </Button>
          {(patient.isCombat || (!(screeningSession && patient.fields.lowVision === "YES"))) && this.scriptShown && renderScript()}
          {screeningSession && patient.fields.lowVision === "YES" && this.scriptShown && <Fragment>
            <Paragraph str="prose.videoScriptSuspiciousEyeDoctor" />
            <Paragraph str="prose.videoScriptOpticNerveBlind" />
          </Fragment>}
        </Card.Pad>
      </Card>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/Video.js